import type { ReactNode } from 'react';

import { Nowrap, Text } from '@farmersdog/corgi-x';

import { categories, ReviewCategory } from './categories';
import {
  AllieSources,
  BlinkySources,
  BrunoSources,
  BuzzSources,
  CaptainSources,
  ChoochSources,
  CrosbySources,
  DesiDaphneHamishSources,
  EmmaSources,
  EvaAndEffieSources,
  FranciscoSources,
  GiovanniSources,
  KoliSources,
  LegendChubbsSources,
  LucySources,
  MjSources,
  OllieSources,
  PickleSources,
  PiperSources,
  RemiSources,
  RosieSources,
  SadieSources,
  SaidiDeanSources,
  SilverRaySources,
  TobeySources,
  ValiSources,
  WinnieSources,
  ZeusSources,
  ZoeSources,
  FranklinSingleSources,
  FranklinBeforeSources,
  FranklinAfterSources,
  PorkchopBeforeSources,
  PorkchopAfterSources,
  DiegoBeforeSources,
  DiegoAfterSources,
  KairoBeforeSources,
  KairoAfterSources,
  HankBeforeSources,
  HankAfterSources,
  MarloBeforeSources,
  MarloAfterSources,
  MaryJaneBeforeSources,
  MaryJaneAfterSources,
  BrodyBeforeSources,
  BrodyAfterSources,
  NillaBeforeSources,
  NillaAfterSources,
  ThorSources,
  BrunoBulldogSources,
} from './assets';

type BaseProps = {
  id: number;
  categories: ReviewCategory[];
};

export type VideoReview = BaseProps & {
  label: string;
  title: ReactNode;
  mobileWistiaId: string;
  desktopWistiaId: string;
  videoTrackingTag: string;
};

export type DoubleImageReview = BaseProps & {
  dogName: string;
  testimonial: ReactNode;
  showBeforeAfterCopy?: boolean;
  doubleImage: [string[], string[]];
  alts: [string, string];
};

export type SingleImageReview = BaseProps & {
  image: string[];
  testimonial: ReactNode;
  dogName: string;
  alt: string;
};

export type ReviewsCarouselItemProps =
  | VideoReview
  | DoubleImageReview
  | SingleImageReview;

export const videoReviewsData: VideoReview[] = [
  {
    id: 1,
    label:
      'Video of Pippa, a once low-energy dog who now has tons of energy thanks to The Farmer’s Dog.',
    mobileWistiaId: 'drj5c4ghbj',
    desktopWistiaId: '4r0q62cxhy',
    title: (
      <>
        Pippa |{' '}
        <Text weight="normal">{categories[ReviewCategory.OverallHealth]}</Text>
      </>
    ),
    videoTrackingTag: `Pippa | ${categories[ReviewCategory.OverallHealth]}`,
    categories: [ReviewCategory.OverallHealth],
  },
  {
    id: 2,
    label:
      'Video of Farmer, a rescued malnourished puppy who transformed within 48 hours after starting The Farmer’s Dog.',
    mobileWistiaId: '06l0jj5qci',
    desktopWistiaId: 'f3z3cn1dvz',
    title: (
      <>
        Farmer |{' '}
        <Text weight="normal">{categories[ReviewCategory.PuppyCare]}</Text>
      </>
    ),
    videoTrackingTag: `Farmer | ${categories[ReviewCategory.PuppyCare]}`,
    categories: [ReviewCategory.PuppyCare],
  },
  {
    id: 3,
    label:
      'Video of Hunnie, a 21-year-old dog who regained energy and appetite thanks to The Farmer’s Dog.',
    mobileWistiaId: 'tteo7dybc4',
    desktopWistiaId: 'nkd3qca38y',
    title: (
      <>
        Hunnie |{' '}
        <Text weight="normal">{categories[ReviewCategory.SeniorCare]}</Text>
      </>
    ),
    videoTrackingTag: `Hunnie | ${categories[ReviewCategory.SeniorCare]}`,
    categories: [ReviewCategory.SeniorCare],
  },
  {
    id: 5,
    label:
      'Video of Maximus, a dog with chronic digestive issues who got better after switching to The Farmer’s Dog.',
    mobileWistiaId: 'tw9s5uyjc0',
    desktopWistiaId: 'b24waz6hhp',
    title: (
      <>
        Maximus |{' '}
        <Text weight="normal">{categories[ReviewCategory.Digestion]}</Text>
      </>
    ),
    videoTrackingTag: `Maximus | ${categories[ReviewCategory.Digestion]}`,
    categories: [ReviewCategory.Digestion],
  },
  {
    id: 4,
    label:
      'Video showing multiple dogs’ weight loss transformations before and after starting The Farmer’s Dog.',
    mobileWistiaId: 'lsuo21xjcq',
    desktopWistiaId: '66mol8k2sp',
    title: (
      <>
        Sailor |{' '}
        <Text weight="normal">{categories[ReviewCategory.SkinAndCoat]}</Text>
      </>
    ),
    videoTrackingTag: `Sailor | ${categories[ReviewCategory.WeightIssues]}`,
    categories: [ReviewCategory.SkinAndCoat],
  },
];

export const copyReviewsData: ReviewsCarouselItemProps[] = [
  {
    id: 6,
    doubleImage: [SaidiDeanSources, SilverRaySources],
    testimonial: (
      <>
        I cannot say enough good things about this food. From the customer
        service to the actual food, it’s a game changer. The best investment
        I’ve made into <Nowrap>my dogs.</Nowrap>
      </>
    ),
    dogName: 'Saidi Dean & Silver Ray',
    categories: [ReviewCategory.OverallHealth],
    alts: [
      'German Shepherd sitting on wood chips.',
      'Brown dog looking up near a bowl of food.',
    ],
  },
  {
    id: 7,
    image: KoliSources,
    testimonial: (
      <>
        He was really having a lot of problems with his gut. I saw a difference
        almost overnight. His coat is so vibrant. Healthy poops, healthy dog. As
        he’s aged, he’s still quite energetic and youthful. I really attribute
        that <Nowrap>to diet.</Nowrap>
      </>
    ),
    dogName: 'Koli',
    categories: [ReviewCategory.OverallHealth, ReviewCategory.Digestion],
    alt: 'Black and white dog lying on a couch with fluffy pillows.',
  },
  {
    id: 8,
    doubleImage: [FranklinBeforeSources, FranklinAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        He went from 100 pounds to 85 in 3 months. His coat looks better;
        thicker, softer, and shinier. He has less stomach issues. Bonus, he
        loves it and never seems bored <Nowrap>of it.</Nowrap>
      </>
    ),
    dogName: 'Franklin',
    categories: [ReviewCategory.OverallHealth, ReviewCategory.WeightIssues],
    alts: [
      'Gray dog sitting on a sandy path, tongue out.',
      'Gray dog standing indoors on a wooden floor.',
    ],
  },
  {
    id: 9,
    image: EmmaSources,
    testimonial: (
      <>
        I just took my 12-year-old chocolate lab for her check up and she has
        lost 5 pounds in one month. She is keeping up with my six month old
        Australian shepherd puppy. <Nowrap>The Farmer’s Dog</Nowrap> is very
        much <Nowrap>worth it.</Nowrap>
      </>
    ),
    dogName: 'Emma',
    categories: [ReviewCategory.OverallHealth, ReviewCategory.WeightIssues],
    alt: 'Two dogs standing on a stone patio, one brown and one merle-coated.',
  },
  {
    id: 10,
    image: PickleSources,
    testimonial: (
      <>
        I can’t recommend this food enough. They make it so easy by packaging it
        in daily amounts, which is determined based off your dog’s particular
        breed, age, and weight goals. No measuring, no preparation, just take it
        out of the fridge <Nowrap>and feed.</Nowrap>
      </>
    ),
    dogName: 'Pickle',
    categories: [ReviewCategory.OverallHealth],
    alt: 'Brown dog sitting on the beach with eyes closed, enjoying the sun.',
  },
  {
    id: 11,
    image: OllieSources,
    testimonial: (
      <>
        His tummy issues are finally under control and everything that{' '}
        <Nowrap> The Farmer’s Dog</Nowrap>
        said would happen on a fresh food diet happened: Ollie is at his optimal
        weight, he has more energy, his coat is shinier, breath is better! He
        even <Nowrap>smiles more!!</Nowrap>
      </>
    ),
    dogName: 'Ollie',
    categories: [ReviewCategory.OverallHealth],
    alt: 'Cream-colored dog lying on a couch with a dark pillow behind.',
  },
  {
    id: 12,
    image: BlinkySources,
    testimonial: (
      <>
        The vet couldn’t get over how healthy he was, that he’s FINALLY down to
        a healthy weight and how clean and white his teeth looked!!! Since he’s
        been eating <Nowrap> The Farmer’s Dog</Nowrap> for 1.5 years he’s like a
        puppy, <Nowrap>full of energy.</Nowrap>
      </>
    ),
    dogName: 'Blinky',
    categories: [ReviewCategory.OverallHealth],
    alt: 'Small black dog with white markings sitting on grass and looking up.',
  },
  {
    id: 13,
    image: TobeySources,
    testimonial: (
      <>
        I absolutely love this food for my 8-month-old puppy!! This food has
        done amazing things for his coat & helps his tummy. This food makes his
        poop the way it’s supposed to be. So glad we tried it & keep on feeding
        this to <Nowrap>my boy!!</Nowrap>
      </>
    ),
    dogName: 'Tobey',
    categories: [ReviewCategory.PuppyCare, ReviewCategory.SkinAndCoat],
    alt: 'White and brown dog sitting on gravel, looking up with ears perked.',
  },
  {
    id: 14,
    image: AllieSources,
    testimonial: (
      <>
        In the 10 months I have had her, she has never been ready to eat, it was
        always a struggle... Allie now is right there ready to eat when her bowl
        reaches the floor. I have also noticed her attitude and energy level are{' '}
        <Nowrap>much better.</Nowrap>
      </>
    ),
    dogName: 'Allie',
    categories: [ReviewCategory.PuppyCare, ReviewCategory.PickyEating],
    alt: 'Corgi standing on a patio, looking up with a happy expression.',
  },
  {
    id: 15,
    image: LucySources,
    testimonial: (
      <>
        This has changed both of our lives for the better. Lucy’s just over a
        year old - and I have never seen her so healthy. I will never{' '}
        <Nowrap>look back!</Nowrap>
      </>
    ),
    dogName: 'Lucy',
    categories: [ReviewCategory.PuppyCare, ReviewCategory.Digestion],
    alt: 'Brown and white speckled dog sitting on sand, looking up with mouth open.',
  },
  {
    id: 16,
    image: ThorSources,
    testimonial: (
      <>
        Thor is 7-months-old. Quick benefits: his poops becoming solid, less
        room clearing farts, having more energy and his hair/fur seems{' '}
        <Nowrap>more vibrant.</Nowrap>
      </>
    ),
    dogName: 'Thor',
    categories: [ReviewCategory.PuppyCare],
    alt: 'Bulldog sitting on a wooden floor in a kitchen, looking up.',
  },
  {
    id: 17,
    image: ValiSources,
    testimonial: (
      <>
        When he was a puppy he had some digestion issues. Before we switched to{' '}
        <Nowrap> The Farmer’s Dog</Nowrap>, it was totally typical for him to
        throw up four times a week. Now, I can’t remember the last time he{' '}
        <Nowrap>threw up.</Nowrap>
      </>
    ),
    dogName: 'Vali',
    categories: [ReviewCategory.PuppyCare],
    alt: 'Black and white dog with tongue out, sitting indoors on a couch.',
  },
  {
    id: 18,
    image: LegendChubbsSources,
    testimonial: (
      <>
        He was low on energy and he was a puppy. I just knew he did not feel
        good at all. Within 2 months of feeding{' '}
        <Nowrap> The Farmer’s Dog</Nowrap>, he has a lot more energy and
        definitely loves his food. Everyone keeps telling me that his name
        doesn’t match <Nowrap>his body!</Nowrap>
      </>
    ),
    dogName: 'Legend Chubb',
    categories: [ReviewCategory.PuppyCare, ReviewCategory.SkinAndCoat],
    alt: 'Pug sitting on a person’s lap, looking directly at the camera.',
  },
  {
    id: 19,
    image: CrosbySources,
    testimonial: (
      <>
        I absolutely love this food for my 8-month-old puppy!! This food has
        done amazing things for his coat & helps his tummy. This food makes his
        poop the way it’s supposed to be. So glad we tried it & keep on feeding
        this to <Nowrap>my boy!!</Nowrap>
      </>
    ),
    dogName: 'Crosby',
    categories: [ReviewCategory.PuppyCare, ReviewCategory.SkinAndCoat],
    alt: 'French Bulldog with black coat and white chest standing on hind legs on a stone patio.',
  },
  {
    id: 20,
    doubleImage: [PorkchopBeforeSources, PorkchopAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        We decided to change her food to <Nowrap> The Farmer’s Dog</Nowrap> and
        her vet said her weight loss is the best thing we could have done for
        her in her senior years. She is 12 now, nobody believes me when I tell
        them that. She looks and acts like when she was{' '}
        <Nowrap>a puppy.</Nowrap>
      </>
    ),
    dogName: 'Porkchop',
    categories: [ReviewCategory.SeniorCare, ReviewCategory.WeightIssues],
    alts: [
      'Overweight corgi lying on a marble hearth in front of a fireplace.',
      'Corgi standing on a wooden floor, looking up with a happy expression.',
    ],
  },
  {
    id: 21,
    image: SadieSources,
    testimonial: (
      <>
        Sadie had started to gain weight and become lethargic... We started her
        on <Nowrap> The Farmer’s Dog</Nowrap> and wow! Today she went to the vet
        and had lost the weight, shiny and soft fur and running around like{' '}
        <Nowrap> a puppy!</Nowrap>
      </>
    ),
    dogName: 'Sadie',
    categories: [ReviewCategory.SeniorCare, ReviewCategory.WeightIssues],
    alt: 'Small tan dog standing on a patterned rug, looking up with mouth slightly open.',
  },
  {
    id: 22,
    image: BuzzSources,
    testimonial: (
      <>
        We have seen incredible improvements in his energy, coat, and overall
        happiness. He looks forward to meals and prances around like a toddler
        expecting dessert when we pull it out of the fridge. We joke he’s aging
        like <Nowrap>Benjamin Button.</Nowrap>
      </>
    ),
    dogName: 'Buzz',
    categories: [ReviewCategory.SeniorCare, ReviewCategory.SkinAndCoat],
    alt: 'Small black and tan dog being held, looking off to the side indoors.',
  },
  {
    id: 23,
    image: MjSources,
    testimonial: (
      <>
        MJ has been loving <Nowrap> The Farmer’s Dog</Nowrap> food for two
        years. She is going on 14-years- old. Vet’s comment last visit: “She
        looks perfect, excellent weight, waist definition and{' '}
        <Nowrap>beautiful coat!</Nowrap>
      </>
    ),
    dogName: 'MJ',
    categories: [ReviewCategory.SeniorCare, ReviewCategory.SkinAndCoat],
    alt: 'Elderly black and white dog sitting on a couch with tongue slightly out.',
  },
  {
    id: 24,
    image: GiovanniSources,
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        HE IS MORE PLAYFUL THAN EVER. At 9-years-old he is no longer lethargic
        and wants to play until I eventually give up. I cannot rave about this
        food enough. It’s worth every penny to see my pup this happy{' '}
        <Nowrap>and healthy.</Nowrap>
      </>
    ),
    dogName: 'Giovanni',
    categories: [ReviewCategory.SeniorCare],
    alt: 'Gray dog sitting on grass next to a person’s legs, panting happily in the sun.',
  },
  {
    id: 25,
    image: RosieSources,
    testimonial: (
      <>
        Rosie had issues with very soft stools—not any more. It’s very
        reasonably priced for how nutritious it is and the fact that it’s human
        grade! It really does smell delicious <Nowrap>as well!!</Nowrap>
      </>
    ),
    dogName: 'Rosie',
    categories: [ReviewCategory.Digestion],
    alt: 'White and gray dog wearing a colorful sweater, sitting on grass with autumn leaves.',
  },
  {
    id: 26,
    image: CaptainSources,
    testimonial: (
      <>
        My elderly dog loves it. His night time stinky flatulence and mucous
        bowel movements have stopped. We were feeding him a high end ‘premium’
        food before so the cost difference <Nowrap>is negligible.</Nowrap>
      </>
    ),
    dogName: 'Captain',
    categories: [ReviewCategory.Digestion],
    alt: 'Small tan and white dog with a bandana sitting on a bed, looking toward the camera.',
  },
  {
    id: 27,
    image: ZoeSources,
    testimonial: (
      <>
        Zoe had a very sensitive stomach. We tried{' '}
        <Nowrap> The Farmer’s Dog</Nowrap> about four months ago and she hasn’t
        had an issue since! She stands at the refrigerator every day at dinner
        time and can’t wait until it’s in her bowl. She always licks the{' '}
        <Nowrap>bowl clean.</Nowrap>
      </>
    ),
    dogName: 'Zoe',
    categories: [ReviewCategory.Digestion],
    alt: 'Small white terrier sitting on a fluffy rug in front of a decorative sign.',
  },
  {
    id: 28,
    image: DesiDaphneHamishSources,
    testimonial: (
      <>
        The proof is in the poop. My dog’s poops are firmer and not as{' '}
        <Nowrap>bad smelling.</Nowrap>
      </>
    ),
    dogName: 'Desi, Daphne & Hamish',
    categories: [ReviewCategory.Digestion],
    alt: 'Fluffy white dog holding a large yellow rope toy in its mouth.',
  },
  {
    id: 29,
    doubleImage: [DiegoBeforeSources, DiegoAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        His stomach was so big it touched the ground when he walked. Couldn’t
        seem to shed the weight for YEARS. He absolutely loves his food and is a
        different dog now! We have not changed anything else. IT IS TRULY{' '}
        <Nowrap>A MIRACLE!</Nowrap>
      </>
    ),
    dogName: 'Diego',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Long-haired white and tan dog sitting on wooden steps with eyes closed.',
      'Same dog standing indoors in sunlight, looking up alertly.',
    ],
  },
  {
    id: 30,
    doubleImage: [KairoBeforeSources, KairoAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        Thank you for being a crucial part in Kairo’s weight loss journey, he’s
        never ran faster, he’s never looked happier and most importantly, he’s
        never <Nowrap>looked healthier!</Nowrap>
      </>
    ),
    dogName: 'Kairo',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Boxer dog sitting indoors in front of a glass door, looking slightly tired.',
      'Same Boxer dog sitting on green grass, looking alert and healthy.',
    ],
  },
  {
    id: 31,
    doubleImage: [HankBeforeSources, HankAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        After 4 months, he is down to his ideal weight! I could not believe the
        scale when I first saw it. I have also noticed a huge difference in
        their coat health. My dogs get so excited for meals now, and I am so
        happy they love this food. Worth <Nowrap>every penny!</Nowrap>
      </>
    ),
    dogName: 'Hank',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Black and white dog lying indoors on a green and yellow mat.',
      'Same dog standing on grass, mouth open, looking playful and energetic.',
    ],
  },
  {
    id: 32,
    doubleImage: [MarloBeforeSources, MarloAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        She gained a few pounds so we decided to make a change in her diet and
        exercise. She lost 7 pounds (a lot for a 32 pound dog!) and everyone
        says she looks like a puppy even though she is 4-years-old. She is full
        of energy and loves <Nowrap>meal time.</Nowrap>
      </>
    ),
    dogName: 'Marlo',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Brown and white dog sitting on a green field, looking up.',
      'Same dog sitting on a dry grassy field at sunset, looking ahead.',
    ],
  },
  {
    id: 33,
    doubleImage: [MaryJaneBeforeSources, MaryJaneAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        My chocolate Lab went from a bloated 99 pounds on processed balls of
        food to 70 pounds after 2 years of real food from{' '}
        <Nowrap> The Farmer’s Dog</Nowrap> already know she will be with us
        longer because <Nowrap>of it!</Nowrap>
      </>
    ),
    dogName: 'Mary Jane',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Brown dog wearing a pink harness, carrying a frisbee on a dirt field.',
      'Same brown dog standing on grass, tongue out and looking happy next to a tennis ball.',
    ],
  },
  {
    id: 34,
    doubleImage: [BrodyBeforeSources, BrodyAfterSources],
    showBeforeAfterCopy: true,
    testimonial: (
      <>
        It’s the absolute best food. He was 102 pounds and is now 85! His vet
        asked us what we were doing and told us to keep it up! Brody is 10 years
        old and he’s like a puppy again. He’s living his{' '}
        <Nowrap>best life!</Nowrap>
      </>
    ),
    dogName: 'Brody',
    categories: [ReviewCategory.WeightIssues],
    alts: [
      'Overweight yellow lab lying on a grassy lawn, facing left.',
      'Slimmer yellow lab standing on snow, looking to the side in a forested area.',
    ],
  },
  {
    id: 35,
    image: ChoochSources,
    testimonial: (
      <>
        He is, out of all the dogs we’ve had, the most picky eater by far. He’ll
        turn down a steak. We were pretty skeptical, and he loves{' '}
        <Nowrap> The Farmer’s Dog.</Nowrap> He lights up at{' '}
        <Nowrap>dinner time.</Nowrap>
      </>
    ),
    dogName: 'Chooch',
    categories: [ReviewCategory.PickyEating],
    alt: 'Curly-haired dog wearing a sweater, lying on a colorful blanket near a Christmas tree.',
  },
  {
    id: 36,
    image: FranciscoSources,
    testimonial: (
      <>
        We thought we were providing great food… He never begged for his meals,
        he ate it but only as a necessity. Now, he is transformed. He loves meal
        time… he is happier, has more energy and just even more precious{' '}
        <Nowrap> than normal.</Nowrap>
      </>
    ),
    dogName: 'Francisco',
    categories: [ReviewCategory.PickyEating],
    alt: 'Small fluffy dog standing on a couch, looking happy with a green toy nearby.',
  },
  {
    id: 37,
    image: WinnieSources,
    testimonial: (
      <>
        For years, mealtime was a battle. I had to stand by her side just to get
        her to eat, and often she would only nibble reluctantly. But now, Winnie
        can’t wait for her meals. It’s such a joy to see her excited{' '}
        <Nowrap>and eager.</Nowrap>
      </>
    ),
    dogName: 'Winnie',
    categories: [ReviewCategory.PickyEating],
    alt: 'Brown curly-haired dog sitting next to a person holding a bag of dog food.',
  },
  {
    id: 38,
    image: PiperSources,
    testimonial: (
      <>
        Finally! Food that Piper enjoys! We’ve started and stopped with a number
        of brands, but now we’re customers for the long haul! He just loves the
        food and we have zero waste. The food looks palatable, even to me. The{' '}
        service has <Nowrap> been extraordinary.</Nowrap>
      </>
    ),
    dogName: 'Piper',
    categories: [ReviewCategory.PickyEating],
    alt: '',
  },
  {
    id: 39,
    image: EvaAndEffieSources,
    testimonial: (
      <>
        Every meal was a battle. Eva hardly ever ate and she was so thin. The
        second that I introduced <Nowrap>The Farmer’s Dog</Nowrap>, she started
        eating every bite. My other little dog, Effie, her energy is sky high!
        Needless to say, we <Nowrap>couldn’t be happier!</Nowrap>
      </>
    ),
    dogName: 'Eva & Effie',
    categories: [ReviewCategory.PickyEating],
    alt: 'Two small white dogs in matching outfits sitting on dark wooden stairs.',
  },
  {
    id: 40,
    image: FranklinSingleSources,
    testimonial: (
      <>
        We are very glad we switched from dry food that seemed to bore him after
        a few days to <Nowrap>The Farmer’s Dog</Nowrap> which he looks forward
        to <Nowrap>every meal!!</Nowrap>
      </>
    ),
    dogName: 'Franklin',
    categories: [ReviewCategory.PickyEating],
    alt: 'Short-haired brown dog standing on a wooden floor, looking directly at the camera.',
  },
  {
    id: 41,
    image: RemiSources,
    testimonial: (
      <>
        He started having allergy problems, all sorts of things that I didn’t
        realize could be attributed to the food we were giving him. As soon as
        we started feeding <Nowrap>The Farmer’s Dog</Nowrap>, he was a
        completely <Nowrap>different dog.</Nowrap>
      </>
    ),
    dogName: 'Remi',
    categories: [ReviewCategory.SkinAndCoat],
    alt: 'Close-up of a brown and white dog’s face, with focus on the eyes and nose.',
  },
  {
    id: 42,
    image: BrunoSources,
    testimonial: (
      <>
        After two months of <Nowrap>The Farmer’s Dog</Nowrap>, he no longer
        sheds and his breath is fresh and clean. His eyes are bright and his
        energy level is through <Nowrap>the roof!</Nowrap>
      </>
    ),
    dogName: 'Bruno',
    categories: [ReviewCategory.OverallHealth, ReviewCategory.SkinAndCoat],
    alt: 'Small white and brown dog sitting on gravel, looking up with a happy expression.',
  },
  {
    id: 43,
    image: BrunoBulldogSources,
    testimonial: (
      <>
        Thanks so much for this dog food! I tried everything to help Bruno’s
        severe allergies and his hair is growing back after{' '}
        <Nowrap>4 weeks!</Nowrap>
      </>
    ),
    dogName: 'Bruno',
    categories: [ReviewCategory.SkinAndCoat],
    alt: 'White bulldog wearing a red bandana, sitting in front of two packs of dog food on a patterned rug.',
  },
  {
    id: 44,
    image: ZeusSources,
    testimonial: (
      <>
        Within six months, nearly all of his hair that was lost has grown back.
        His fur is healthier than ever, so is his attitude and activity level.
        The difference was so apparent that other dog owners noticed Zeus’s
        crisp coat <Nowrap>of fur.</Nowrap>
      </>
    ),
    dogName: 'Zeus',
    categories: [ReviewCategory.SkinAndCoat],
    alt: 'Smiling French Bulldog wearing angel wings, sitting on a carpeted floor.',
  },
  {
    id: 45,
    doubleImage: [NillaBeforeSources, NillaAfterSources],
    testimonial: (
      <>
        The first picture was before we started giving her{' '}
        <Nowrap>The Farmer’s Dog.</Nowrap> Then, after about 6 to 8 weeks, her
        belly <Nowrap>cleared up!!</Nowrap>
      </>
    ),
    dogName: 'Nilla',
    categories: [ReviewCategory.SkinAndCoat],
    alts: [
      'Dog’s belly with visible red bumps and irritation before treatment.',
      'Dog’s belly after treatment, showing reduced redness and smoother skin.',
    ],
  },
  {
    id: 46,
    image: WinnieSources,
    testimonial: (
      <>
        Winnie had skin irritations and frequent eye infections. Within just a
        couple of weeks after starting <Nowrap>The Farmer’s Dog</Nowrap>, his
        skin and eyes were clear. Our groomer was amazed! We could not be
        happier, and he is like a <Nowrap>new dog!</Nowrap>
      </>
    ),
    dogName: 'Winnie',
    categories: [ReviewCategory.SkinAndCoat],
    alt: 'Doodle dog sitting on kitchen floor next to a bag of The Farmer’s Dog food held by a person.',
  },
];

export const reviewsData: ReviewsCarouselItemProps[] = [
  ...videoReviewsData,
  ...copyReviewsData,
];
